import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Stack } from "@seneca/senekit";

import LoadingSpinner from "features/common/spinners/LoadingSpinner";

import useAnonymousUnsubscribe from "../../hooks/useAnonymousUnsubscribe";
import { useUpdatePreferences } from "../../hooks/useUpdatePreferences";
import { EmailSettings } from "./EmailSettings";
import { UnsubscribeResult } from "./UnsubscribeResult";

export function UnsubscribePageContent() {
  const { encryptedContactKey } = useParams<{ encryptedContactKey: string }>();
  const {
    data,
    isSuccess,
    isError,
    isLoading,
    mutate: unsubscribe
  } = useAnonymousUnsubscribe(decodeURIComponent(encryptedContactKey));
  const {
    mutate: updatePreferences,
    isSuccess: isUpdateSuccess,
    isLoading: isUpdateLoading,
    isError: isUpdateError
  } = useUpdatePreferences(decodeURIComponent(encryptedContactKey));

  useEffect(() => {
    unsubscribe();
  }, [unsubscribe]);

  if (isLoading || isUpdateLoading) {
    return <LoadingSpinner loading colour="dark" size="medium" />;
  }

  if (isError || isUpdateError) {
    return (
      <UnsubscribeResult heading="Something went wrong" isSuccess={false}>
        You were not unsubscribed successfully - try reloading the page.
      </UnsubscribeResult>
    );
  }

  if (isUpdateSuccess) {
    return (
      <UnsubscribeResult heading="Preferences successfully updated" isSuccess>
        You can manage your communication preferences at any time from your
        settings page by logging into your Seneca account
      </UnsubscribeResult>
    );
  }

  if (isSuccess) {
    return (
      <Stack space="xxlarge">
        <UnsubscribeResult heading="Successfully unsubscribed!" isSuccess>
          You will no longer receive these emails.
        </UnsubscribeResult>
        <EmailSettings
          initialContactPreferences={data}
          hasLoadedInitialContactPreferences={isSuccess}
          updatePreferences={updatePreferences}
        />
      </Stack>
    );
  }

  return null;
}
